import type {
  LookContentEditorSection,
} from '../../../../components/LookContentEditor';

export const PLACEHOLDER =
  'https://prod-assets.lyskraft.in/custom/a711dbe9-9118-416f-a820-72e8f4bbc3f2.png';

export type EditableSection = LookContentEditorSection | null;

type WorkflowRecommendationInput = {
  product_type?: string;
  description?: string;
  mapped_lk_styles?: Array<string | number>;
  mapped_product_images?: Record<string, string | object | undefined>;
};

export type WorkflowRecommendation = {
  product_type: string;
  description: string;
  mapped_lk_styles: Array<string>;
  mapped_product_images: Record<string, string | object | undefined>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

export const deepMergeRecord = (
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> => {
  const output: Record<string, unknown> = { ...target };

  Object.entries(source).forEach(([key, sourceValue]) => {
    const targetValue = output[key];
    if (isRecord(targetValue) && isRecord(sourceValue)) {
      output[key] = deepMergeRecord(targetValue, sourceValue);
      return;
    }
    output[key] = sourceValue;
  });

  return output;
};

export const normalizeWorkflowRecommendations = (
  recommendations: unknown,
): Array<WorkflowRecommendation> => {
  if (!Array.isArray(recommendations)) {
    return [];
  }

  return recommendations.map(item => {
    const recommendation = (item ?? {}) as WorkflowRecommendationInput;
    return {
      product_type: recommendation.product_type || '',
      description: recommendation.description || '',
      mapped_lk_styles: Array.isArray(recommendation.mapped_lk_styles)
        ? recommendation.mapped_lk_styles.map(value => String(value))
        : [],
      mapped_product_images:
        recommendation.mapped_product_images &&
        typeof recommendation.mapped_product_images === 'object'
          ? recommendation.mapped_product_images
          : {},
    };
  });
};

export const getEditorSectionFor = (
  section: EditableSection,
): LookContentEditorSection | undefined => {
  if (!section) {
    return undefined;
  }
  return section;
};

export const getEditingSectionTitle = (section: EditableSection) => {
  if (!section) {
    return '';
  }
  if (section === 'occasion') return 'Occasion';
  if (section === 'look_name') return 'Look Name';
  if (section === 'website') return 'Website Content';
  if (section === 'pdp') return 'PDP Content';
  return 'Search Seeds';
};

export const getBackoffDelayMs = (attempt: number) => {
  const baseDelayMs = 1500;
  const maxDelayMs = 12000;
  return Math.min(baseDelayMs * 2 ** Math.min(attempt, 4), maxDelayMs);
};

export type ModelTagConfig = {
  label: string;
  tone: 'success' | 'info' | 'warning' | 'neutral';
  withSparkle?: boolean;
};

export const getModelTagConfig = (model?: string | null): ModelTagConfig | null => {
  if (!model) {
    return null;
  }

  const modelName = model.toLowerCase();
  if (modelName.includes('gpt-image') || modelName.startsWith('openai/')) {
    return { label: 'OpenAI', tone: 'success', withSparkle: true };
  }
  if (modelName.includes('gemini')) {
    return { label: 'Gemini', tone: 'info' };
  }
  if (modelName.includes('nano_banana')) {
    return { label: 'Nano Banana', tone: 'warning' };
  }

  return { label: model, tone: 'neutral' };
};
